import React, { useState } from "react";
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Icon,
  useToast,
  Textarea,
  Radio,
  RadioGroup,
  Stack,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Flex,
} from "@chakra-ui/react";
import { FaTimes, FaArrowLeft } from "react-icons/fa";
import { XCircle, AlertTriangle, ArrowLeft } from "lucide-react";
import axios from "axios";
import { BASE_URL } from "../../config/RequestMethod";

interface DeclineFormProps {
  back: (value: boolean) => void;
  proposalId: string | null;
  onSuccess: () => void;
}

const DECLINE_REASONS = [
  "Budget constraints - The pricing exceeds our current budget",
  "Timeline concerns - The proposed timeline doesn't align with our needs",
  "Found alternative solution - We decided to go with another provider",
  "Scope mismatch - The proposal doesn't fully meet our requirements",
  "Internal decision - Management decided to handle this internally",
  "Delayed project - We've decided to postpone this project",
  "Other",
];

const DeclineForm: React.FC<DeclineFormProps> = ({
  back,
  proposalId,
  onSuccess,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cancelRef = React.useRef(null);
  const toast = useToast();

  const openConfirmModal = () => {
    // Validation
    if (!selectedReason) {
      toast({
        title: "Please select a reason",
        description: "Choose why you're declining this proposal",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    if (selectedReason === "Other" && !customReason.trim()) {
      toast({
        title: "Custom reason required",
        description: "Please provide your reason for declining",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setIsDialogOpen(true);
  };

  const handleConfirm = async () => {
    setIsDialogOpen(false);
    setIsSubmitting(true);

    try {
      const payload = {
        declineReason: selectedReason,
        customReason: customReason.trim(),
      };

      const res = await axios.put(
        `${BASE_URL}client-proposal/${proposalId}/decline`,
        payload
      );

      toast({
        title: "Proposal Declined",
        description: res?.data?.message || "Thank you for your feedback",
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      // Call success callback
      setTimeout(() => {
        onSuccess();
      }, 1000);
    } catch (error: any) {
      console.error("Error:", error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.message || "Failed to decline proposal",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box maxW="900px" w="full" mx="auto" py={8}>
        {/* Modern Header */}
        <Box
          bg="white"
          borderRadius="2xl"
          boxShadow="xl"
          border="2px solid"
          borderColor="red.100"
          p={8}
          mb={6}
          position="relative"
          overflow="hidden"
        >
          {/* Background Gradient Accent */}
          <Box
            position="absolute"
            top="-100px"
            right="-100px"
            w="300px"
            h="300px"
            borderRadius="full"
            bgGradient="linear(to-br, red.100, orange.100)"
            opacity={0.3}
            filter="blur(60px)"
          />

          <VStack spacing={6} position="relative" zIndex={1}>
            {/* Icon & Title */}
            <Flex align="center" gap={4}>
              <Box
                bgGradient="linear(to-br, red.500, red.600)"
                p={4}
                borderRadius="2xl"
                boxShadow="lg"
              >
                <Icon as={XCircle} w={8} h={8} color="white" />
              </Box>
              <VStack align="start" spacing={0}>
                <Text fontSize="2xl" fontWeight="900" color="gray.800">
                  Decline Proposal
                </Text>
                <Text fontSize="sm" color="gray.500" fontWeight="500">
                  Please let us know why you're declining
                </Text>
              </VStack>
            </Flex>

            {/* Decline Reasons */}
            <Box w="full">
              <Text
                fontSize="sm"
                fontWeight="600"
                color="gray.700"
                mb={3}
                display="flex"
                alignItems="center"
                gap={2}
              >
                <Box w="4px" h="4px" borderRadius="full" bg="red.500" />
                Select Reason for Declining
              </Text>

              <RadioGroup value={selectedReason} onChange={setSelectedReason}>
                <Stack spacing={3}>
                  {DECLINE_REASONS.map((reason, index) => (
                    <Box
                      key={index}
                      border="2px solid"
                      borderColor={
                        selectedReason === reason ? "red.400" : "gray.200"
                      }
                      borderRadius="xl"
                      p={4}
                      cursor="pointer"
                      transition="all 0.2s"
                      bg={selectedReason === reason ? "red.50" : "white"}
                      _hover={{
                        borderColor: "red.300",
                        transform: "translateX(4px)",
                      }}
                      onClick={() => setSelectedReason(reason)}
                    >
                      <Radio value={reason} colorScheme="red">
                        <Text
                          fontSize="sm"
                          fontWeight={selectedReason === reason ? "600" : "500"}
                          color={
                            selectedReason === reason ? "red.900" : "gray.700"
                          }
                        >
                          {reason}
                        </Text>
                      </Radio>
                    </Box>
                  ))}
                </Stack>
              </RadioGroup>
            </Box>

            {/* Custom Reason Input (shown only when "Other" is selected) */}
            {selectedReason === "Other" && (
              <Box
                w="full"
                animation="fadeIn 0.3s ease-in"
                style={{
                  animation: "fadeIn 0.3s ease-in",
                }}
              >
                <Text
                  fontSize="sm"
                  fontWeight="600"
                  color="gray.700"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <Box w="4px" h="4px" borderRadius="full" bg="orange.500" />
                  Please Specify Your Reason
                </Text>
                <Textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Type your reason for declining here..."
                  size="lg"
                  borderRadius="xl"
                  bg="white"
                  border="2px solid"
                  borderColor="gray.200"
                  _hover={{ borderColor: "orange.300" }}
                  _focus={{
                    borderColor: "orange.500",
                    boxShadow: "0 0 0 1px #F97316",
                    bg: "white",
                  }}
                  p={4}
                  minH="120px"
                  resize="vertical"
                  transition="all 0.2s"
                  fontSize="sm"
                />
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {customReason.length} characters
                </Text>
              </Box>
            )}

            {/* Action Buttons */}
            <Flex w="full" gap={4} pt={4}>
              <Button
                leftIcon={<ArrowLeft size={16} />}
                onClick={() => back(false)}
                size="lg"
                flex={1}
                fontSize="sm"
                fontWeight="700"
                variant="outline"
                borderWidth="2px"
                borderColor="gray.300"
                color="gray.700"
                _hover={{
                  borderColor: "gray.400",
                  bg: "gray.50",
                }}
                isDisabled={isSubmitting}
              >
                Go Back
              </Button>
              <Button
                leftIcon={<XCircle size={16} />}
                onClick={openConfirmModal}
                size="lg"
                flex={1}
                fontSize="sm"
                fontWeight="700"
                bgGradient="linear(to-r, red.400, red.600)"
                color="white"
                _hover={{
                  bgGradient: "linear(to-r, red.500, red.700)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 20px -4px rgba(239, 68, 68, 0.5)",
                }}
                _active={{
                  transform: "translateY(0)",
                }}
                transition="all 0.2s"
                isLoading={isSubmitting}
                loadingText="Submitting..."
              >
                Decline Proposal
              </Button>
            </Flex>
          </VStack>
        </Box>

        {/* Info Box */}
        <Box
          bg="orange.50"
          border="1px solid"
          borderColor="orange.200"
          borderRadius="xl"
          p={4}
        >
          <HStack spacing={3} align="start">
            <Icon as={AlertTriangle} w={5} h={5} color="orange.600" mt={0.5} />
            <VStack align="start" spacing={1}>
              <Text fontSize="sm" fontWeight="700" color="orange.900">
                Important Notice
              </Text>
              <Text fontSize="xs" color="orange.800" lineHeight="1.6">
                Your feedback is valuable to us. This helps us understand your
                needs better and improve our proposals. This action is{" "}
                <strong>final</strong> and <strong>cannot be undone</strong>.
              </Text>
            </VStack>
          </HStack>
        </Box>
      </Box>

      {/* Confirmation Modal */}
      <AlertDialog
        isOpen={isDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDialogOpen(false)}
        isCentered
        motionPreset="slideInBottom"
      >
        <AlertDialogOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
        <AlertDialogContent
          borderRadius="2xl"
          mx={4}
          boxShadow="2xl"
          border="1px solid"
          borderColor="red.100"
        >
          <AlertDialogHeader
            fontSize="lg"
            fontWeight="900"
            borderBottom="1px solid"
            borderColor="gray.100"
            pb={4}
          >
            <Flex align="center" gap={3}>
              <Box
                bgGradient="linear(to-br, red.400, red.600)"
                p={2}
                borderRadius="xl"
              >
                <Icon as={XCircle} w={5} h={5} color="white" />
              </Box>
              <Text>Confirm Decline</Text>
            </Flex>
          </AlertDialogHeader>

          <AlertDialogBody py={6}>
            <VStack align="stretch" spacing={4}>
              <Box
                bg="orange.50"
                border="1px solid"
                borderColor="orange.200"
                borderRadius="lg"
                p={4}
              >
                <HStack spacing={2} mb={2}>
                  <Icon as={AlertTriangle} w={4} h={4} color="orange.600" />
                  <Text fontSize="sm" fontWeight="700" color="orange.900">
                    This action cannot be undone
                  </Text>
                </HStack>
                <Text fontSize="xs" color="orange.800" lineHeight="1.6">
                  Once you proceed, this proposal will be marked as{" "}
                  <strong>DECLINED</strong> and the owner will be notified.
                </Text>
              </Box>

              <Box
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                p={3}
              >
                <Text fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                  Your reason:
                </Text>
                <Text fontSize="sm" color="gray.800" fontWeight="500">
                  {selectedReason === "Other" ? customReason : selectedReason}
                </Text>
              </Box>

              <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                Are you sure you want to decline this proposal?
              </Text>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter
            borderTop="1px solid"
            borderColor="gray.100"
            pt={4}
          >
            <Button
              ref={cancelRef}
              onClick={() => setIsDialogOpen(false)}
              size="md"
              variant="ghost"
              mr={3}
            >
              Cancel
            </Button>
            <Button
              leftIcon={<FaTimes />}
              onClick={handleConfirm}
              size="md"
              bgGradient="linear(to-r, red.400, red.600)"
              color="white"
              _hover={{
                bgGradient: "linear(to-r, red.500, red.700)",
              }}
              fontWeight="700"
            >
              Yes, Decline Proposal
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DeclineForm;
